import { apiClientV2, unwrapV2Response } from './index.js';

/**
 * v2 Admin user-management API.
 *
 * Wraps the `/v2/admin/users` endpoints on `apiClientV2`. All responses use the
 * v2 envelope `{ success, message, data, errors }`; each helper unwraps `data`
 * via `unwrapV2Response` and throws on `success: false` so React Query treats it
 * as an error.
 *
 * Migrated from the legacy `admin.js` (axios) equivalents. Notable v2 changes:
 * - The user is identified by `uuid` (the Cosmos object id was dropped).
 * - The paginated list returns `items` (v1 returned `users`).
 * - `verified_by_admin` is set through the update endpoint (the separate v1
 *   "approve user" endpoint is folded into the PATCH).
 * - Email verification is a dedicated endpoint taking `email_address` in the body.
 */

const base = '/admin/users';

/**
 * Lists users (paginated). Mirrors the v1 `adminGetUsers` return shape by
 * mapping the v2 `items` array back to `users` so existing table code keeps
 * working during migration.
 *
 * @returns {Promise<{ users: object[], total: number, offset: number, limit: number }>}
 */
export const adminGetUsersV2 = async ({
	offset = 0,
	limit = 100,
	search = '',
	tier = '',
	role = '',
	sort_by = 'created_at',
	sort_order = 'desc',
	detailed = false,
} = {}) => {
	// Only send params that have a value; the v2 API treats absent as "no filter".
	// sort_by/sort_order come from URL params that are `null` when absent (the
	// destructuring default only covers `undefined`), so fall back explicitly.
	const query = {
		offset,
		limit,
		sort_by: sort_by || 'created_at',
		sort_order: sort_order || 'desc',
		detailed,
	};
	if (search) query.search = search;
	if (tier) query.tier = tier;
	if (role) query.role = role;

	const { data, error } = await apiClientV2.get(base, query);
	if (error) throw error;

	const page = unwrapV2Response(data);
	return {
		users: page?.items ?? [],
		total: page?.total ?? 0,
		offset: page?.offset ?? offset,
		limit: page?.limit ?? limit,
	};
};

/**
 * Partially updates a user. Also used to approve/unapprove a user by passing
 * `verified_by_admin`.
 *
 * @param {string} uuid - The user's uuid.
 * @param {object} updateData - Any subset of { role, tier, access, trial_expiry, verified_by_admin }.
 */
export const adminUpdateUserV2 = async (uuid, updateData) => {
	const { data, error } = await apiClientV2.patch(`${base}/${uuid}`, updateData);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Deletes a user account by uuid.
 *
 * @param {string} uuid - The user's uuid.
 */
export const adminDeleteUserV2 = async uuid => {
	const { data, error } = await apiClientV2.delete(`${base}/${uuid}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Verifies a user's email address. Email is passed in the request body.
 *
 * @param {string} email_address - The user's email address.
 * @returns {Promise<{ email_address: string, verified: boolean }>}
 */
export const adminVerifyUserEmailV2 = async email_address => {
	const { data, error } = await apiClientV2.put(`${base}/verify_email`, {
		email_address,
	});
	if (error) throw error;
	return unwrapV2Response(data);
};
